import { Fragment } from "react";
import { HomeOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslations } from "use-intl";
import { TreeNodeIcon } from "../../views/tree-node-icon/tree-node-icon";
import { CompanyDocumentTreeNode } from "../../model/types";
import { nodeTitle } from "../../utils/node-title";
import { useDroppable } from "@dnd-kit/core";
import {
  ancestorDropId,
  LEVEL_PANEL_DROP_ID,
  ROOT_DROP_ID,
} from "../../utils/resolve-drop-target";
import { LevelDropLink } from "./level-drop-link";

/**
 * Путь перетаскиваемого узла «Корень › … › родитель», закреплённый у низа окна:
 * поднять узел на любой уровень выше можно, не прокручивая дерево к нужной папке.
 */
export function LevelDropPanel({
  ancestors,
  dropTargetKey,
}: {
  /** Родители узла от верхнего уровня до непосредственного. */
  ancestors: CompanyDocumentTreeNode[];
  dropTargetKey?: string;
}) {
  const t = useTranslations("CompanyDocumentFolder");
  const tc = useTranslations("Common");
  const parent = ancestors.at(-1);
  // Приёмник и сама панель: см. `levelPanelCollision`.
  const { setNodeRef } = useDroppable({ id: LEVEL_PANEL_DROP_ID });

  // Ниже копии узла: DragOverlay из dnd-kit рисуется с z-index 999. Над полосой
  // автопрокрутки у края окна (`AUTO_SCROLL` в дереве), чтобы прицеливание в звено
  // не прокручивало страницу.
  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-[10vh] z-[998] flex justify-center">
      <div
        ref={setNodeRef}
        className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-xl border border-[--palette-neutral-2] bg-white p-3 shadow-lg"
      >
        <span className="px-1 text-[--palette-neutral-6]">{t("Move to")}</span>

        <LevelDropLink
          id={ROOT_DROP_ID}
          icon={<HomeOutlined />}
          title={t("Root folder")}
          current={!parent}
          dropTarget={dropTargetKey === ROOT_DROP_ID}
        />

        {ancestors.map((ancestor) => (
          <Fragment key={ancestor.id}>
            <RightOutlined className="text-xs text-[--palette-neutral-5]" />
            <LevelDropLink
              id={ancestorDropId(ancestor.id)}
              icon={<TreeNodeIcon node={ancestor} />}
              title={nodeTitle(ancestor, tc("Hidden"))}
              current={ancestor.id === parent?.id}
              dropTarget={dropTargetKey === ancestor.id}
            />
          </Fragment>
        ))}
      </div>
    </div>
  );
}
